import * as THREE from 'three';
import { Panel } from '../ui/panel';
import type { PanelButton } from '../ui/button';
import { acquireMenuPointer, releaseMenuPointer } from '../ui/pointer';
import { placePanelInFront } from '../ui/placement';
import { t } from '../i18n';
import { loadUserHeightCm, saveUserHeightCm, detectHeightFromCameraY } from './heightPanel';
import { loadDominantHand, saveDominantHand } from './handPanel';
import { inclinazioneLabel, latoLabel, filoLabel } from '../session/strokeLabels';
import { guardLabel } from '../session/guardLabels';
import type { DrillTarget } from '../session/repEvaluator';
import {
  ARCADE_DURATION_MS,
  CAMERA_TRACKED_MIN_Y_M,
  DIFFICULTY_DEFAULT,
  DIFFICULTY_ORDER,
  DOMINANT_HAND,
  DRILL_TARGET_FILIGRANA_ALPHA,
  DRILL_MIN_EDGE_DEFAULT,
  DRILL_REPS_OPTIONS,
  DRILL_TYPE,
  DURATION_MODE,
  FILO,
  GUARD,
  HAND,
  HEIGHT_DETECT_OVERRIDE_THRESHOLD_CM,
  INCLINAZIONE,
  LATO,
  SESSION_DURATION_OPTIONS_MS,
  SETTINGS_HEIGHT_MAX_CM,
  SETTINGS_HEIGHT_MIN_CM,
  SETTINGS_HEIGHT_STEP_CM,
  TECHNIQUE,
  WIZARD_PANEL_HEIGHT_M,
  WIZARD_PANEL_WIDTH_M,
  ZONE,
  type Difficulty,
  type DominantHand,
  type DrillType,
  type DurationMode,
  type Filo,
  type Guard,
  type GuardMode,
  type Inclinazione,
  type Lato,
  type Technique,
  type Zone,
} from '../config/constants';
import { difficultyLabel } from '../i18n';

export type { DurationMode };

/** Sentinel a wizard step resolves with when the user taps "Indietro". */
const BACK = Symbol('wizard-back');
type Back = typeof BACK;

export type SessionConfig = {
  userHeightCm: number;
  dominantHand: DominantHand;
  drillType: DrillType;
  durationMode: DurationMode;
  /** 0 in free/reps mode (no timer). */
  durationMs: number;
  /** Present ⇒ prescriptive drill (pell mode + a target colpo to score each rep against). */
  prescriptiveTarget?: DrillTarget;
  /** Correct-rep goal for the prescriptive drill (durationMode === 'reps'). */
  repsTarget?: number;
  /** Guard mode: practice every guard ('all') or one specific guardia. */
  guardMode?: GuardMode;
  /** Arcade only: which gesture resolves the targets — cut ("Sfida") or thrust ("punte"). */
  technique?: Technique;
  /** Arcade only: difficulty preset, or `custom` (manual sliders, never scored) — #43. */
  difficulty?: Difficulty;
};

/** Which drill the user picked, and which pell variant. */
type DrillVariant = 'normal' | 'prescriptive';
type DrillChoice = { drillType: DrillType; variant: DrillVariant };

export class SetupWizard {
  private panel: Panel | null = null;
  /** Whether this wizard currently holds the shared menu pointer (idempotent release). */
  private pointerHeld = false;
  private extraSelectListener: ((e: XRInputSourceEvent) => void) | null = null;
  private tickFn: (() => void) | null = null;

  constructor(
    private scene: THREE.Scene,
    private camera: THREE.Camera,
    private session: XRSession,
  ) {}

  async run(): Promise<SessionConfig> {
    this.acquirePointer();

    let userHeightCm = loadUserHeightCm();
    let dominantHand = loadDominantHand();
    let drill: DrillChoice = { drillType: DRILL_TYPE.pell, variant: 'normal' };
    let prescriptiveTarget: DrillTarget | undefined;

    // Sequential steps with "Indietro": height → hand → drill → [target] → terminator.
    let step = 0;
    for (;;) {
      if (step === 0) {
        userHeightCm = await this.runHeightStep();
        saveUserHeightCm(userHeightCm);
        step = 1;
      } else if (step === 1) {
        const r = await this.runHandStep(true);
        if (r === BACK) {
          step = 0;
          continue;
        }
        dominantHand = r;
        saveDominantHand(dominantHand);
        step = 2;
      } else if (step === 2) {
        const r = await this.runDrillTypeStep(true);
        if (r === BACK) {
          step = 1;
          continue;
        }
        drill = r;
        step = 3;
      } else if (step === 3) {
        prescriptiveTarget = undefined;
        if (drill.variant === 'prescriptive') {
          const r = await this.runDrillTargetStep(true);
          if (r === BACK) {
            step = 2;
            continue;
          }
          prescriptiveTarget = r;
        }
        step = 4;
      } else {
        const backTo = drill.variant === 'normal' ? 2 : 3;
        // Guard mode is observational + untimed: pick scope (all / one), then launch free.
        if (drill.drillType === DRILL_TYPE.guard) {
          const gm = await this.runGuardScopeFlow();
          if (gm === BACK) {
            step = backTo;
            continue;
          }
          this.dispose();
          return {
            userHeightCm,
            dominantHand,
            drillType: DRILL_TYPE.guard,
            durationMode: DURATION_MODE.free,
            durationMs: 0,
            guardMode: gm,
          };
        }
        // Arcade ("Sfida") hub: pick technique (cut / thrust) + duration (60s / free) in one step.
        if (drill.drillType === DRILL_TYPE.arcade) {
          const r = await this.runArcadeConfigStep(true);
          if (r === BACK) {
            step = backTo;
            continue;
          }
          this.dispose();
          return {
            userHeightCm,
            dominantHand,
            drillType: DRILL_TYPE.arcade,
            durationMode: r.durationMode,
            durationMs: r.durationMs,
            technique: r.technique,
            difficulty: r.difficulty,
          };
        }
        if (drill.variant === 'prescriptive') {
          const r = await this.runRepsCountStep(true);
          if (r === BACK) {
            step = backTo;
            continue;
          }
          this.dispose();
          return {
            userHeightCm,
            dominantHand,
            drillType: drill.drillType,
            durationMode: DURATION_MODE.reps,
            durationMs: 0,
            repsTarget: r,
            ...(prescriptiveTarget ? { prescriptiveTarget } : {}),
          };
        }
        const r = await this.runDurationStep(drill.drillType, true);
        if (r === BACK) {
          step = backTo;
          continue;
        }
        this.dispose();
        return {
          userHeightCm,
          dominantHand,
          drillType: drill.drillType,
          durationMode: r.durationMode,
          durationMs: r.durationMs,
        };
      }
    }
  }

  /**
   * Skip height/hand steps. Re-runs drill-type + duration so the user can switch
   * (e.g. from pell-timed to targets-5min) without going through height/hand again.
   */
  async runDurationOnly(): Promise<{
    drillType: DrillType;
    durationMode: DurationMode;
    durationMs: number;
    prescriptiveTarget?: DrillTarget;
    repsTarget?: number;
    guardMode?: GuardMode;
    technique?: Technique;
    difficulty?: Difficulty;
  }> {
    this.acquirePointer();

    let drill: DrillChoice = { drillType: DRILL_TYPE.pell, variant: 'normal' };
    let prescriptiveTarget: DrillTarget | undefined;

    // drill is the entry step here (no height/hand before it) → no "Indietro" on it.
    let step = 0;
    for (;;) {
      if (step === 0) {
        drill = (await this.runDrillTypeStep(false)) as DrillChoice;
        step = 1;
      } else if (step === 1) {
        prescriptiveTarget = undefined;
        if (drill.variant === 'prescriptive') {
          const r = await this.runDrillTargetStep(true);
          if (r === BACK) {
            step = 0;
            continue;
          }
          prescriptiveTarget = r;
        }
        step = 2;
      } else {
        const backTo = drill.variant === 'normal' ? 0 : 1;
        if (drill.drillType === DRILL_TYPE.guard) {
          const gm = await this.runGuardScopeFlow();
          if (gm === BACK) {
            step = backTo;
            continue;
          }
          this.dispose();
          return {
            drillType: DRILL_TYPE.guard,
            durationMode: DURATION_MODE.free,
            durationMs: 0,
            guardMode: gm,
          };
        }
        // Arcade ("Sfida") hub: pick technique (cut / thrust) + duration (60s / free) in one step.
        if (drill.drillType === DRILL_TYPE.arcade) {
          const r = await this.runArcadeConfigStep(true);
          if (r === BACK) {
            step = backTo;
            continue;
          }
          this.dispose();
          return {
            drillType: DRILL_TYPE.arcade,
            durationMode: r.durationMode,
            durationMs: r.durationMs,
            technique: r.technique,
            difficulty: r.difficulty,
          };
        }
        if (drill.variant === 'prescriptive') {
          const r = await this.runRepsCountStep(true);
          if (r === BACK) {
            step = backTo;
            continue;
          }
          this.dispose();
          return {
            drillType: drill.drillType,
            durationMode: DURATION_MODE.reps,
            durationMs: 0,
            repsTarget: r,
            ...(prescriptiveTarget ? { prescriptiveTarget } : {}),
          };
        }
        const r = await this.runDurationStep(drill.drillType, true);
        if (r === BACK) {
          step = backTo;
          continue;
        }
        this.dispose();
        return {
          drillType: drill.drillType,
          durationMode: r.durationMode,
          durationMs: r.durationMs,
        };
      }
    }
  }

  tick(): void {
    this.tickFn?.();
  }

  dispose(): void {
    this.releasePointer();
    this.disposePanel();
    this.unbindExtraSelect();
    this.tickFn = null;
  }

  // ---- steps ----

  private runHeightStep(): Promise<number> {
    return new Promise((resolve) => {
      const panel = this.spawnPanel();
      const cw = panel.canvas.width;
      const ch = panel.canvas.height;
      let cm = loadUserHeightCm();
      const camY = this.camera.position.y;
      if (camY > CAMERA_TRACKED_MIN_Y_M) {
        const detected = detectHeightFromCameraY(camY);
        if (Math.abs(detected - cm) > HEIGHT_DETECT_OVERRIDE_THRESHOLD_CM) cm = detected;
      }

      const repaint = (): void => {
        panel.setButtons([
          {
            rect: { x: cw * 0.1, y: ch * 0.55, w: cw * 0.22, h: ch * 0.22 },
            label: '−5 cm',
            variant: 'secondary',
            onClick: () => {
              cm = Math.max(SETTINGS_HEIGHT_MIN_CM, cm - SETTINGS_HEIGHT_STEP_CM);
              repaint();
            },
          },
          {
            rect: { x: cw * 0.39, y: ch * 0.55, w: cw * 0.22, h: ch * 0.22 },
            label: '+5 cm',
            variant: 'secondary',
            onClick: () => {
              cm = Math.min(SETTINGS_HEIGHT_MAX_CM, cm + SETTINGS_HEIGHT_STEP_CM);
              repaint();
            },
          },
          {
            rect: { x: cw * 0.68, y: ch * 0.55, w: cw * 0.22, h: ch * 0.22 },
            label: 'OK',
            variant: 'primary',
            onClick: () => resolve(cm),
          },
        ]);
        panel.render(t('heightDetected')(cm), t('confirmHeight'));
      };

      repaint();
    });
  }

  private runHandStep(canBack: boolean): Promise<DominantHand | Back> {
    return new Promise((resolve) => {
      const panel = this.spawnPanel();
      const cw = panel.canvas.width;
      const ch = panel.canvas.height;
      let detected: DominantHand = loadDominantHand();
      let phase: 'await' | 'confirm' = 'await';

      const goBack = (): void => {
        this.unbindExtraSelect();
        resolve(BACK);
      };

      const showConfirm = (): void => {
        phase = 'confirm';
        const buttons: PanelButton[] = [
          {
            rect: { x: cw * 0.15, y: ch * 0.6, w: cw * 0.32, h: ch * 0.22 },
            label: t('changeHand'),
            variant: 'secondary',
            onClick: () => {
              detected =
                detected === DOMINANT_HAND.right ? DOMINANT_HAND.left : DOMINANT_HAND.right;
              showConfirm();
            },
          },
          {
            rect: { x: cw * 0.53, y: ch * 0.6, w: cw * 0.32, h: ch * 0.22 },
            label: 'OK',
            variant: 'primary',
            onClick: () => {
              this.unbindExtraSelect();
              resolve(detected);
            },
          },
        ];
        if (canBack) buttons.push(this.backButton(goBack, cw, ch));
        panel.setButtons(buttons);
        panel.render(
          detected === DOMINANT_HAND.right ? t('handDetectedRight') : t('handDetectedLeft'),
          t('handChangeHint'),
        );
      };

      this.bindExtraSelect((e) => {
        if (phase !== 'await') return;
        const h = e.inputSource.handedness;
        if (h !== HAND.left && h !== HAND.right) return;
        detected = h;
        showConfirm();
      });

      panel.setButtons(canBack ? [this.backButton(goBack, cw, ch)] : []);
      panel.render(t('squeezeTrigger'), '');
    });
  }

  private runDrillTypeStep(canBack: boolean): Promise<DrillChoice | Back> {
    return new Promise((resolve) => {
      const panel = this.spawnPanel();
      const cw = panel.canvas.width;
      const ch = panel.canvas.height;

      // 2×2 grid: pell / sfida (arcade), drill / guardie. (Targets were folded into arcade — #44.)
      const btnW = cw * 0.4;
      const btnH = ch * 0.15;
      const cols = [0.07, 0.53];
      const rows = [0.3, 0.55];
      const opts: Array<{ label: string; choice: DrillChoice; variant: 'primary' | 'secondary' }> =
        [
          {
            label: t('drillTypePell'),
            choice: { drillType: DRILL_TYPE.pell, variant: 'normal' },
            variant: 'primary',
          },
          {
            label: t('drillTypeArcade'),
            choice: { drillType: DRILL_TYPE.arcade, variant: 'normal' },
            variant: 'primary',
          },
          {
            label: t('drillTypePrescriptive'),
            choice: { drillType: DRILL_TYPE.pell, variant: 'prescriptive' },
            variant: 'secondary',
          },
          {
            label: t('drillTypeGuard'),
            choice: { drillType: DRILL_TYPE.guard, variant: 'normal' },
            variant: 'secondary',
          },
        ];
      const buttons: PanelButton[] = opts.map((o, i) => ({
        rect: {
          x: cw * (cols[i % 2] ?? 0),
          y: ch * (rows[Math.floor(i / 2)] ?? 0),
          w: btnW,
          h: btnH,
        },
        label: o.label,
        variant: o.variant,
        onClick: () => resolve(o.choice),
      }));
      if (canBack) buttons.push(this.backButton(() => resolve(BACK), cw, ch));
      panel.setButtons(buttons);
      panel.render(t('drillTypeLabel'), '');
    });
  }

  /**
   * Arcade hub config: three stacked selectors — technique (cut / thrust), duration
   * (60s "Sfida" / free practice) and difficulty (4 presets + Custom) — then "Avanti". Custom
   * reads the manual sliders and is never scored (#43).
   */
  private runArcadeConfigStep(canBack: boolean): Promise<
    | {
        technique: Technique;
        durationMode: DurationMode;
        durationMs: number;
        difficulty: Difficulty;
      }
    | Back
  > {
    return new Promise((resolve) => {
      const panel = this.spawnPanel();
      const cw = panel.canvas.width;
      const ch = panel.canvas.height;

      let technique: Technique = TECHNIQUE.cut;
      let timed = true; // 60s "Sfida" vs free practice
      let difficulty: Difficulty = DIFFICULTY_DEFAULT;

      const cols = [0.1, 0.53];
      const btnW = cw * 0.37;
      const btnH = ch * 0.12;

      const repaint = (): void => {
        const buttons: PanelButton[] = [];
        const techOpts: Array<[Technique, string]> = [
          [TECHNIQUE.cut, t('arcadeTechniqueCut')],
          [TECHNIQUE.thrust, t('arcadeTechniqueThrust')],
        ];
        techOpts.forEach(([v, label], i) =>
          buttons.push({
            rect: { x: cw * (cols[i] ?? 0), y: ch * 0.26, w: btnW, h: btnH },
            label,
            variant: v === technique ? 'primary' : 'secondary',
            onClick: () => {
              technique = v;
              repaint();
            },
          }),
        );
        const durOpts: Array<[boolean, string]> = [
          [true, t('arcadeDurationTimed')],
          [false, t('arcadeDurationFree')],
        ];
        durOpts.forEach(([v, label], i) =>
          buttons.push({
            rect: { x: cw * (cols[i] ?? 0), y: ch * 0.42, w: btnW, h: btnH },
            label,
            variant: v === timed ? 'primary' : 'secondary',
            onClick: () => {
              timed = v;
              repaint();
            },
          }),
        );
        // Difficulty: a row of 5 chips (4 presets + Custom).
        const diffMargin = 0.05;
        const diffGap = 0.015;
        const diffW =
          (1 - 2 * diffMargin - diffGap * (DIFFICULTY_ORDER.length - 1)) / DIFFICULTY_ORDER.length;
        DIFFICULTY_ORDER.forEach((d, i) => {
          buttons.push({
            rect: {
              x: cw * (diffMargin + i * (diffW + diffGap)),
              y: ch * 0.58,
              w: cw * diffW,
              h: ch * 0.11,
            },
            label: difficultyLabel(d),
            variant: d === difficulty ? 'primary' : 'secondary',
            onClick: () => {
              difficulty = d;
              repaint();
            },
          });
        });
        buttons.push({
          rect: { x: cw * 0.37, y: ch * 0.73, w: cw * 0.26, h: ch * 0.11 },
          label: t('next'),
          variant: 'primary',
          onClick: () =>
            resolve({
              technique,
              durationMode: timed ? DURATION_MODE.timed : DURATION_MODE.free,
              durationMs: timed ? ARCADE_DURATION_MS : 0,
              difficulty,
            }),
        });
        if (canBack) buttons.push(this.backButton(() => resolve(BACK), cw, ch));
        panel.setButtons(buttons);
        panel.render(t('arcadeConfigLabel'), '');
      };

      repaint();
    });
  }

  /**
   * Prescriptive target picker: each axis (lato/inclinazione/filo/zona) pins a value OR
   * "qualsiasi" (undefined → not scored). Defaults to all-qualsiasi; the user taps only the
   * dimensions that matter. "Avanti" → rep-count step.
   */
  private runDrillTargetStep(canBack: boolean): Promise<DrillTarget | Back> {
    return new Promise((resolve) => {
      // Stronger segno here: it is the period diagram of the very cut names being picked.
      const panel = this.spawnPanel(DRILL_TARGET_FILIGRANA_ALPHA);
      const cw = panel.canvas.width;
      const ch = panel.canvas.height;

      let lato: Lato | undefined;
      let inclinazione: Inclinazione | undefined;
      let filo: Filo | undefined;
      let zone: Zone | undefined;

      // A row of options; `undefined` is the "qualsiasi" chip (last in each row).
      const row = <T>(
        opts: ReadonlyArray<T | undefined>,
        cur: T | undefined,
        label: (v: T) => string,
        y: number,
        set: (v: T | undefined) => void,
      ): PanelButton[] => {
        const n = opts.length;
        const w = (1 - 0.08) / n - 0.012;
        return opts.map((v, i) => ({
          rect: { x: cw * (0.04 + i * (w + 0.012)), y: ch * y, w: cw * w, h: ch * 0.1 },
          label: v === undefined ? t('optAny') : label(v),
          variant: v === cur ? 'primary' : 'secondary',
          onClick: () => {
            set(v);
            repaint();
          },
        }));
      };

      const repaint = (): void => {
        // Fendente/montante are sideless → no lato choice (force "qualsiasi", show only that chip).
        const sideless =
          inclinazione === INCLINAZIONE.fendente || inclinazione === INCLINAZIONE.montante;
        if (sideless) lato = undefined;
        const latoOpts = sideless ? [undefined] : [LATO.mandritto, LATO.riverso, undefined];

        const buttons: PanelButton[] = [
          ...row(latoOpts, lato, latoLabel, 0.2, (v) => (lato = v)),
          ...row(
            [
              INCLINAZIONE.fendente,
              INCLINAZIONE.sgualembro,
              INCLINAZIONE.tondo,
              INCLINAZIONE.ridoppio,
              INCLINAZIONE.montante,
              undefined,
            ],
            inclinazione,
            inclinazioneLabel,
            0.33,
            (v) => (inclinazione = v),
          ),
          ...row([FILO.dritto, FILO.falso, undefined], filo, filoLabel, 0.46, (v) => (filo = v)),
          ...row(
            [ZONE.head, ZONE.shoulder, ZONE.chest, ZONE.flank, ZONE.leg, undefined],
            zone,
            zoneLabel,
            0.59,
            (v) => (zone = v),
          ),
          {
            rect: { x: cw * 0.37, y: ch * 0.73, w: cw * 0.26, h: ch * 0.1 },
            label: t('next'),
            variant: 'primary',
            onClick: () =>
              resolve({
                minEdge: DRILL_MIN_EDGE_DEFAULT,
                ...(lato !== undefined ? { lato } : {}),
                ...(inclinazione !== undefined ? { inclinazione } : {}),
                ...(filo !== undefined ? { filo } : {}),
                ...(zone !== undefined ? { zone } : {}),
              }),
          },
        ];
        if (canBack) buttons.push(this.backButton(() => resolve(BACK), cw, ch));
        panel.setButtons(buttons);
        panel.render(t('drillTargetLabel'), '');
      };

      repaint();
    });
  }

  /** Rep-count goal for the prescriptive drill (5 / 10 / 20). */
  private runRepsCountStep(canBack: boolean): Promise<number | Back> {
    return new Promise((resolve) => {
      const panel = this.spawnPanel();
      const cw = panel.canvas.width;
      const ch = panel.canvas.height;

      const n = DRILL_REPS_OPTIONS.length;
      const w = 0.2;
      const gap = (1 - n * w) / (n + 1);
      const buttons: PanelButton[] = DRILL_REPS_OPTIONS.map((reps, i) => ({
        rect: { x: cw * (gap + i * (w + gap)), y: ch * 0.45, w: cw * w, h: ch * 0.18 },
        label: `${reps}`,
        variant: 'primary',
        onClick: () => resolve(reps),
      }));
      if (canBack) buttons.push(this.backButton(() => resolve(BACK), cw, ch));
      panel.setButtons(buttons);
      panel.render(t('drillRepsLabel'), '');
    });
  }

  /** Guard mode scope: choose "all" or a single guardia (the picker has its own back to here). */
  private async runGuardScopeFlow(): Promise<GuardMode | Back> {
    for (;;) {
      const scope = await this.runGuardScopeStep();
      if (scope === BACK) return BACK;
      if (scope === 'all') return 'all';
      const picked = await this.runGuardPickStep();
      if (picked !== BACK) return picked;
      // BACK from the picker → re-show the scope step.
    }
  }

  private runGuardScopeStep(): Promise<'all' | 'one' | Back> {
    return new Promise((resolve) => {
      const panel = this.spawnPanel();
      const cw = panel.canvas.width;
      const ch = panel.canvas.height;
      const buttons: PanelButton[] = [
        {
          rect: { x: cw * 0.1, y: ch * 0.45, w: cw * 0.35, h: ch * 0.2 },
          label: t('guardScopeAll'),
          variant: 'primary',
          onClick: () => resolve('all'),
        },
        {
          rect: { x: cw * 0.55, y: ch * 0.45, w: cw * 0.35, h: ch * 0.2 },
          label: t('guardScopeOne'),
          variant: 'primary',
          onClick: () => resolve('one'),
        },
        this.backButton(() => resolve(BACK), cw, ch),
      ];
      panel.setButtons(buttons);
      panel.render(t('guardScopeLabel'), '');
    });
  }

  private runGuardPickStep(): Promise<Guard | Back> {
    return new Promise((resolve) => {
      const panel = this.spawnPanel();
      const cw = panel.canvas.width;
      const ch = panel.canvas.height;
      const guards: Guard[] = [
        GUARD.portaDiFerroStretta,
        GUARD.portaDiFerroLarga,
        GUARD.codaLungaStretta,
        GUARD.codaLungaAlta,
        GUARD.codaLungaLarga,
        GUARD.codaLungaDistesa,
      ];
      const cols = [0.06, 0.52];
      const rowsY = [0.26, 0.45, 0.64];
      const btnW = cw * 0.42;
      const btnH = ch * 0.14;
      const buttons: PanelButton[] = guards.map((g, i) => ({
        rect: {
          x: cw * (cols[i % 2] ?? 0),
          y: ch * (rowsY[Math.floor(i / 2)] ?? 0),
          w: btnW,
          h: btnH,
        },
        label: guardLabel(g),
        variant: 'secondary',
        onClick: () => resolve(g),
      }));
      buttons.push(this.backButton(() => resolve(BACK), cw, ch));
      panel.setButtons(buttons);
      panel.render(t('guardPickLabel'), '');
    });
  }

  private runDurationStep(
    _drillType: DrillType,
    canBack: boolean,
  ): Promise<{ durationMode: DurationMode; durationMs: number } | Back> {
    return new Promise((resolve) => {
      const panel = this.spawnPanel();
      const cw = panel.canvas.width;
      const ch = panel.canvas.height;

      const timed = [
        { label: t('duration30s'), ms: SESSION_DURATION_OPTIONS_MS[0] ?? 30_000 },
        { label: t('duration5'), ms: SESSION_DURATION_OPTIONS_MS[1] ?? 5 * 60_000 },
        { label: t('duration10'), ms: SESSION_DURATION_OPTIONS_MS[2] ?? 10 * 60_000 },
        { label: t('duration15'), ms: SESSION_DURATION_OPTIONS_MS[3] ?? 15 * 60_000 },
      ];

      const btnW = cw * 0.18;
      const btnH = ch * 0.15;
      const timedXs = [0.08, 0.295, 0.51, 0.725];

      const buttons: PanelButton[] = timed.map((d, i) => ({
        rect: { x: cw * (timedXs[i] ?? 0), y: ch * 0.45, w: btnW, h: btnH },
        label: d.label,
        variant: 'primary',
        onClick: () => resolve({ durationMode: DURATION_MODE.timed, durationMs: d.ms }),
      }));
      // Free (test) mode available for both pell and targets.
      buttons.push({
        rect: { x: cw * 0.41, y: ch * 0.68, w: cw * 0.18, h: btnH },
        label: t('durationFree'),
        variant: 'secondary',
        onClick: () => resolve({ durationMode: DURATION_MODE.free, durationMs: 0 }),
      });
      if (canBack) buttons.push(this.backButton(() => resolve(BACK), cw, ch));

      panel.setButtons(buttons);
      panel.render(t('durationLabel'), '');
    });
  }

  // ---- helpers ----

  /** Bottom-left "Indietro" button returning to the previous wizard step. */
  private backButton(onClick: () => void, cw: number, ch: number): PanelButton {
    return {
      rect: { x: cw * 0.06, y: ch * 0.85, w: cw * 0.2, h: ch * 0.1 },
      label: t('back'),
      variant: 'secondary',
      onClick,
    };
  }

  private acquirePointer(): void {
    if (this.pointerHeld) return;
    this.pointerHeld = true;
    acquireMenuPointer();
  }

  private releasePointer(): void {
    if (!this.pointerHeld) return;
    this.pointerHeld = false;
    releaseMenuPointer();
  }

  private spawnPanel(filigranaAlpha?: number): Panel {
    this.disposePanel();
    const panel = new Panel({
      widthM: WIZARD_PANEL_WIDTH_M,
      heightM: WIZARD_PANEL_HEIGHT_M,
      ...(filigranaAlpha !== undefined ? { filigranaAlpha } : {}),
    });
    placePanelInFront(panel.mesh, this.camera);
    this.scene.add(panel.mesh);
    this.panel = panel;
    return panel;
  }

  private disposePanel(): void {
    if (!this.panel) return;
    this.scene.remove(this.panel.mesh);
    this.panel.dispose();
    this.panel = null;
  }

  private bindExtraSelect(fn: (e: XRInputSourceEvent) => void): void {
    this.unbindExtraSelect();
    this.session.addEventListener('selectstart', fn);
    this.extraSelectListener = fn;
  }

  private unbindExtraSelect(): void {
    if (this.extraSelectListener) {
      this.session.removeEventListener('selectstart', this.extraSelectListener);
      this.extraSelectListener = null;
    }
  }
}

/** Display label for a pell zone (d'arte zone names). */
function zoneLabel(z: Zone): string {
  switch (z) {
    case ZONE.head:
      return t('zoneHead');
    case ZONE.shoulder:
      return t('zoneShoulder');
    case ZONE.chest:
      return t('zoneChest');
    case ZONE.flank:
      return t('zoneFlank');
    case ZONE.leg:
      return t('zoneLeg');
  }
}
